import { FormEvent, useState } from 'react';
import useTableOrder from 'hooks/tableOrder';
import { Patient } from 'types/patient';
import { api } from 'services/api';
import { Paginated } from 'types/paginated';
import { formatDate } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';

export type PatientQueryParams = {
  active: boolean;
  // nome ou CPF/CNPJ completo; separado em `name`/`document_number` na hora de buscar
  search: string;
};

// Texto só com dígitos e pontuação de documento (11 ou 14 dígitos) é CPF/CNPJ; o resto é nome.
function splitSearch(search: string) {
  const text = search.trim();
  const digits = text.replace(/\D/g, '');

  if (/^[\d.\-/\s]+$/.test(text) && (digits.length === 11 || digits.length === 14)) {
    const masked =
      digits.length === 11
        ? digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
        : digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');

    return { name: '', document_number: masked };
  }

  return { name: text, document_number: '' };
}

export function useFetchPatients() {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);
  const [orderedIndex, sort] = useTableOrder();
  // a lista só carrega quando o usuário busca: o cadastro pode ter muitos pacientes
  const [searched, setSearched] = useState(false);
  const [filter, setFilter] = useState<PatientQueryParams>({
    search: '',
    active: true,
  });

  function fetchData(nextPage = page, nextRows = rows, nextFilter = filter) {
    setLoading(true);
    setSearched(true);

    api
      .get<Paginated<Patient[]>>('/patients', {
        params: {
          ...splitSearch(nextFilter.search),
          active: nextFilter.active ? 1 : 0,
          page: nextPage + 1,
          rows: nextRows,
        },
      })
      .then(response => {
        setTotal(response.data.total);
        const _patients = response.data.items.map(patient => ({
          ...patient,
          formattedActive: patient.active ? 'Ativo' : 'Inativo',
          formattedCityState: [patient.address?.city, patient.address?.state].filter(Boolean).join(' - '),
          formattedCreatedAt: formatDate(patient.created_at || new Date(), 'P', { locale: ptBR }),
        }));
        setPatients(_patients);
      })
      .catch(err => toast.error(err.response.data.error))
      .finally(() => setLoading(false));
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchData(0, rows, filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
    fetchData(nextPage, rows, filter);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
    fetchData(0, nextRows, filter);
  }

  function handleSort(index: string) {
    const p = sort(index, patients);
    setPatients(p);
  }

  function handleChange(index: keyof PatientQueryParams, value: string | boolean) {
    setFilter(state => ({
      ...state,
      [index]: value,
    }));
  }

  return {
    patients,
    loading,
    orderedIndex,
    handleSort,
    filter,
    handleChange,
    setPage: handleChangePage,
    setRows: handleChangeRows,
    handleSearch,
    searched,
    page,
    rows,
    total,
  };
}
